import React, { useState, useMemo } from 'react';
import { Col, Row, convertToKRW } from '../../lib/utils';
import { MediumText, RegularText, DemiLightText } from '../../lib/Text';
import { CaptionLight } from '../../lib/font';
import { colors } from '../../lib/colors';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../../components/Table/Table';
import SvgIcon from '../../components/SvgIcon';

// ── Types ──
type CardStatus = '등록완료' | '미등록';
type MembershipStatus = '활성' | '만료' | '일시정지';

interface RappoPayMemberItem {
    memberId: string;
    memberName: string;
    coachName: string;
    membershipName: string;
    currentSession: number;
    totalSession: number;
    perPaymentAmount: number;
    cardStatus: CardStatus;
    cardRegisteredDate: string | null;
    lastPaymentDate: string | null;
    totalPaidAmount: number;
    remainingAmount: number;
    paymentMethod: string | null;
    membershipStatus: MembershipStatus;
}

// ── Dummy Data ──
const dummyMembers: RappoPayMemberItem[] = [
    {
        memberId: '1',
        memberName: '김효빈',
        coachName: '홍길동',
        membershipName: 'PT 30회',
        currentSession: 18,
        totalSession: 30,
        perPaymentAmount: 15000,
        cardStatus: '등록완료',
        cardRegisteredDate: '2026-01-15',
        lastPaymentDate: '2026-03-25',
        totalPaidAmount: 270000,
        remainingAmount: 180000,
        paymentMethod: '신한카드',
        membershipStatus: '활성',
    },
    {
        memberId: '2',
        memberName: '이영진',
        coachName: '박철수',
        membershipName: 'PT 10회',
        currentSession: 3,
        totalSession: 10,
        perPaymentAmount: 20000,
        cardStatus: '등록완료',
        cardRegisteredDate: '2026-02-20',
        lastPaymentDate: '2026-03-20',
        totalPaidAmount: 60000,
        remainingAmount: 140000,
        paymentMethod: '삼성카드',
        membershipStatus: '활성',
    },
    {
        memberId: '3',
        memberName: '박지우',
        coachName: '홍길동',
        membershipName: 'PT 20회',
        currentSession: 20,
        totalSession: 20,
        perPaymentAmount: 18000,
        cardStatus: '등록완료',
        cardRegisteredDate: '2025-11-05',
        lastPaymentDate: '2026-03-10',
        totalPaidAmount: 360000,
        remainingAmount: 0,
        paymentMethod: '현대카드',
        membershipStatus: '만료',
    },
    {
        memberId: '4',
        memberName: '최도연',
        coachName: '김민수',
        membershipName: 'PT 50회',
        currentSession: 12,
        totalSession: 50,
        perPaymentAmount: 12000,
        cardStatus: '등록완료',
        cardRegisteredDate: '2026-03-01',
        lastPaymentDate: '2026-03-24',
        totalPaidAmount: 144000,
        remainingAmount: 456000,
        paymentMethod: 'KB국민카드',
        membershipStatus: '활성',
    },
    {
        memberId: '5',
        memberName: '정수현',
        coachName: '박철수',
        membershipName: 'PT 30회',
        currentSession: 0,
        totalSession: 30,
        perPaymentAmount: 15000,
        cardStatus: '미등록',
        cardRegisteredDate: null,
        lastPaymentDate: null,
        totalPaidAmount: 0,
        remainingAmount: 450000,
        paymentMethod: null,
        membershipStatus: '활성',
    },
    {
        memberId: '6',
        memberName: '한서연',
        coachName: '홍길동',
        membershipName: 'PT 10회',
        currentSession: 7,
        totalSession: 10,
        perPaymentAmount: 22000,
        cardStatus: '등록완료',
        cardRegisteredDate: '2026-01-28',
        lastPaymentDate: '2026-03-18',
        totalPaidAmount: 154000,
        remainingAmount: 66000,
        paymentMethod: '롯데카드',
        membershipStatus: '활성',
    },
    {
        memberId: '7',
        memberName: '윤재호',
        coachName: '김민수',
        membershipName: 'PT 20회',
        currentSession: 5,
        totalSession: 20,
        perPaymentAmount: 17000,
        cardStatus: '등록완료',
        cardRegisteredDate: '2026-02-10',
        lastPaymentDate: '2026-03-22',
        totalPaidAmount: 85000,
        remainingAmount: 255000,
        paymentMethod: '우리카드',
        membershipStatus: '일시정지',
    },
    {
        memberId: '8',
        memberName: '송민지',
        coachName: '박철수',
        membershipName: 'PT 30회',
        currentSession: 0,
        totalSession: 30,
        perPaymentAmount: 15000,
        cardStatus: '미등록',
        cardRegisteredDate: null,
        lastPaymentDate: null,
        totalPaidAmount: 0,
        remainingAmount: 450000,
        paymentMethod: null,
        membershipStatus: '활성',
    },
    {
        memberId: '9',
        memberName: '강태현',
        coachName: '홍길동',
        membershipName: 'PT 10회',
        currentSession: 10,
        totalSession: 10,
        perPaymentAmount: 20000,
        cardStatus: '등록완료',
        cardRegisteredDate: '2025-12-20',
        lastPaymentDate: '2026-02-28',
        totalPaidAmount: 200000,
        remainingAmount: 0,
        paymentMethod: '신한카드',
        membershipStatus: '만료',
    },
    {
        memberId: '10',
        memberName: '오하늘',
        coachName: '김민수',
        membershipName: 'PT 50회',
        currentSession: 25,
        totalSession: 50,
        perPaymentAmount: 12000,
        cardStatus: '등록완료',
        cardRegisteredDate: '2025-10-15',
        lastPaymentDate: '2026-03-26',
        totalPaidAmount: 300000,
        remainingAmount: 300000,
        paymentMethod: 'KB국민카드',
        membershipStatus: '활성',
    },
];

const dummySummary = {
    pendingSettlement: { amount: 1250000, count: 8 },
    todaySettlement: { amount: 340000, count: 3 },
    nextSettlement: { date: '2026-03-28', amount: 520000 },
    monthlyTotal: { amount: 2150000, count: 42 },
    monthlySettled: { amount: 1680000, count: 34 },
    overdue: { amount: 470000, count: 5 },
};

// ── Column Definitions ──
const columnHelper = createColumnHelper<RappoPayMemberItem>();

const columns = [
    columnHelper.accessor('memberName', {
        id: 'memberName',
        header: '회원명',
        cell: (info) => info.getValue(),
        size: 90,
        enableSorting: false,
    }),
    columnHelper.accessor('coachName', {
        id: 'coachName',
        header: '강사명',
        cell: (info) => info.getValue(),
        size: 90,
        enableSorting: false,
    }),
    columnHelper.accessor('membershipName', {
        id: 'membershipName',
        header: '수강권명',
        cell: (info) => info.getValue(),
        size: 100,
        enableSorting: false,
    }),
    columnHelper.display({
        id: 'sessionInfo',
        header: '회차 정보',
        cell: (info) => `${info.row.original.currentSession}/${info.row.original.totalSession}회`,
        size: 90,
    }),
    columnHelper.accessor('perPaymentAmount', {
        id: 'perPaymentAmount',
        header: '건당 결제금액',
        cell: (info) => convertToKRW(info.getValue()),
        size: 110,
        enableSorting: false,
    }),
    columnHelper.accessor('cardStatus', {
        id: 'cardStatus',
        header: '카드 등록',
        cell: (info) => {
            const status = info.getValue();
            return (
                <span
                    className={`px-[8px] py-[3px] rounded-[4px] text-[13px] ${
                        status === '등록완료'
                            ? 'bg-[#E8F3FF] text-[#3D73DD]'
                            : 'bg-[#FFF0F0] text-[#E54545]'
                    }`}
                >
                    {status}
                </span>
            );
        },
        size: 90,
        enableSorting: false,
    }),
    columnHelper.accessor('cardRegisteredDate', {
        id: 'cardRegisteredDate',
        header: '등록일',
        cell: (info) => info.getValue() ?? '-',
        size: 100,
        enableSorting: false,
    }),
    columnHelper.accessor('lastPaymentDate', {
        id: 'lastPaymentDate',
        header: '최근 결제일',
        cell: (info) => info.getValue() ?? '-',
        size: 100,
        enableSorting: false,
    }),
    columnHelper.accessor('totalPaidAmount', {
        id: 'totalPaidAmount',
        header: '누적 결제액',
        cell: (info) => convertToKRW(info.getValue()),
        size: 110,
        enableSorting: false,
    }),
    columnHelper.accessor('remainingAmount', {
        id: 'remainingAmount',
        header: '결제 예정액',
        cell: (info) => convertToKRW(info.getValue()),
        size: 110,
        enableSorting: false,
    }),
    columnHelper.accessor('paymentMethod', {
        id: 'paymentMethod',
        header: '결제수단',
        cell: (info) => info.getValue() ?? '-',
        size: 100,
        enableSorting: false,
    }),
];

// ── Summary Card Component ──
const SummaryCard = ({
    title,
    amount,
    sub,
    highlight,
}: {
    title: string;
    amount: string;
    sub: string;
    highlight?: boolean;
}) => (
    <Col
        className={`flex flex-col flex-1 rounded-[16px] p-[20px] ${
            highlight ? 'bg-[#FFF0F0] border border-[#FDDEDE]' : 'bg-white border border-[#E8EBF0]'
        }`}
    >
        <DemiLightText fontSize={13} lineHeight={18} color="#6B7684" className="mb-[8px]">
            {title}
        </DemiLightText>
        <MediumText
            fontSize={22}
            lineHeight={30}
            color={highlight ? '#E54545' : '#3D4244'}
            className="mb-[4px]"
        >
            {amount}
        </MediumText>
        <CaptionLight style={{ fontSize: 12, lineHeight: '16px', color: '#8B95A1' }}>
            {sub}
        </CaptionLight>
    </Col>
);

// ── Main Component ──
const RappoPayHistoryPage = () => {
    const [coachFilter, setCoachFilter] = useState('');
    const [memberFilter, setMemberFilter] = useState('');
    const [cardStatusFilter, setCardStatusFilter] = useState<'전체' | CardStatus>('전체');
    const [membershipStatusFilter, setMembershipStatusFilter] = useState<'전체' | MembershipStatus>('전체');

    const filteredData = useMemo(() => {
        return dummyMembers.filter((item) => {
            if (coachFilter && !item.coachName.includes(coachFilter)) return false;
            if (memberFilter && !item.memberName.includes(memberFilter)) return false;
            if (cardStatusFilter !== '전체' && item.cardStatus !== cardStatusFilter) return false;
            if (membershipStatusFilter !== '전체' && item.membershipStatus !== membershipStatusFilter) return false;
            return true;
        });
    }, [coachFilter, memberFilter, cardStatusFilter, membershipStatusFilter]);

    return (
        <Col className="flex flex-col w-full">
            {/* 요약 카드 */}
            <Row className="flex gap-[12px] mb-[28px]">
                <SummaryCard
                    title="지급 예정 정산액"
                    amount={convertToKRW(dummySummary.pendingSettlement.amount)}
                    sub={`${dummySummary.pendingSettlement.count}건`}
                />
                <SummaryCard
                    title="오늘 지급 예정액"
                    amount={convertToKRW(dummySummary.todaySettlement.amount)}
                    sub={`${dummySummary.todaySettlement.count}건`}
                />
                <SummaryCard
                    title="다음 지급 예정"
                    amount={convertToKRW(dummySummary.nextSettlement.amount)}
                    sub={dummySummary.nextSettlement.date}
                />
                <SummaryCard
                    title="이번 달 총 결제금액"
                    amount={convertToKRW(dummySummary.monthlyTotal.amount)}
                    sub={`${dummySummary.monthlyTotal.count}건`}
                />
                <SummaryCard
                    title="이번 달 정산 완료액"
                    amount={convertToKRW(dummySummary.monthlySettled.amount)}
                    sub={`${dummySummary.monthlySettled.count}건`}
                />
                <SummaryCard
                    title="미수금 (연체)"
                    amount={convertToKRW(dummySummary.overdue.amount)}
                    sub={`${dummySummary.overdue.count}건`}
                    highlight
                />
            </Row>

            {/* 필터 */}
            <Row className="flex items-center gap-[12px] mb-[20px] flex-wrap">
                <Row className="flex items-center bg-white border border-[#E8EBF0] rounded-[8px] px-[12px] h-[40px]">
                    <SvgIcon name="SvgSearch" size={16} fill="#8B95A1" className="mr-[8px]" />
                    <input
                        className="outline-none text-[14px] w-[120px]"
                        placeholder="강사명 검색"
                        value={coachFilter}
                        onChange={(e) => setCoachFilter(e.target.value)}
                    />
                </Row>
                <Row className="flex items-center bg-white border border-[#E8EBF0] rounded-[8px] px-[12px] h-[40px]">
                    <SvgIcon name="SvgSearch" size={16} fill="#8B95A1" className="mr-[8px]" />
                    <input
                        className="outline-none text-[14px] w-[120px]"
                        placeholder="회원명 검색"
                        value={memberFilter}
                        onChange={(e) => setMemberFilter(e.target.value)}
                    />
                </Row>
                <select
                    className="bg-white border border-[#E8EBF0] rounded-[8px] px-[12px] h-[40px] text-[14px] text-[#3D4244] outline-none cursor-pointer"
                    value={cardStatusFilter}
                    onChange={(e) => setCardStatusFilter(e.target.value as any)}
                >
                    <option value="전체">카드 등록: 전체</option>
                    <option value="등록완료">등록완료</option>
                    <option value="미등록">미등록</option>
                </select>
                <select
                    className="bg-white border border-[#E8EBF0] rounded-[8px] px-[12px] h-[40px] text-[14px] text-[#3D4244] outline-none cursor-pointer"
                    value={membershipStatusFilter}
                    onChange={(e) => setMembershipStatusFilter(e.target.value as any)}
                >
                    <option value="전체">수강권 상태: 전체</option>
                    <option value="활성">활성</option>
                    <option value="만료">만료</option>
                    <option value="일시정지">일시정지</option>
                </select>

                {/* 엑셀 다운로드 */}
                <button
                    className="ml-auto flex items-center bg-[#2D3132] rounded-[8px] px-[14px] h-[40px] cursor-pointer"
                    onClick={() => {
                        // TODO: API 연동 후 엑셀 다운로드 구현
                    }}
                >
                    <RegularText fontSize={14} lineHeight={20} color="white">
                        엑셀 다운로드
                    </RegularText>
                </button>
            </Row>

            {/* 테이블 */}
            {filteredData.length > 0 ? (
                <Table
                    columns={columns}
                    data={filteredData}
                    className="mb-[100px]"
                    isPagination
                    customPaginationComponent={({
                        firstPage,
                        nextPage,
                        canNextPage,
                        previousPage,
                        canPreviousPage,
                    }) => (
                        <Row className="absolute items-center flex right-0 bottom-0 mb-[50px]">
                            <RegularText
                                fontSize={16}
                                lineHeight={23}
                                color="#3D4244"
                                className={`mr-[16px] ${
                                    canPreviousPage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                                onClick={() => !canPreviousPage && firstPage()}
                            >
                                처음으로
                            </RegularText>
                            <button
                                className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={previousPage}
                                disabled={canPreviousPage}
                            >
                                <SvgIcon
                                    name="SvgLeftStrokeArrow"
                                    size={15}
                                    fill={colors.trans}
                                    stroke="#70747C"
                                    strokeWidth={1.2}
                                    className="mr-[7px]"
                                />
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    이전
                                </RegularText>
                            </button>
                            <button
                                className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={nextPage}
                                disabled={canNextPage}
                            >
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244" className="mr-[7px]">
                                    다음
                                </RegularText>
                                <SvgIcon
                                    name="SvgRightStrokeArrow"
                                    size={15}
                                    fill={colors.trans}
                                    stroke="#70747C"
                                    strokeWidth={1.2}
                                />
                            </button>
                        </Row>
                    )}
                />
            ) : (
                <Col className="flex items-center justify-center h-[300px]">
                    <RegularText fontSize={16} lineHeight={24} color="#B6B8BA">
                        조건에 맞는 데이터가 없습니다
                    </RegularText>
                </Col>
            )}
        </Col>
    );
};

export default RappoPayHistoryPage;
